import styled from 'styled-components';
import { theme } from '../theme';

// Main container
export const SessionManagementContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.light};
`;

// Header section
export const SessionManagementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e7eb;
`;

export const SessionManagementTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: 24px;
  font-weight: 600;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 5px;
  border-radius: ${theme.borderRadius.sm};
  transition: background-color ${theme.transitions.normal};

  &:hover {
    background-color: #f3f4f6;
  }
`;

// Loading and error states
export const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 16px;
`;

export const ErrorMessage = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: ${theme.colors.error};
  padding: 12px 16px;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ErrorCloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.error};
  cursor: pointer;
  font-size: 16px;
`;

// Session info section
export const SessionInfo = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: ${theme.borderRadius.md};
  padding: 16px;
  margin-bottom: 24px;
`;

export const SessionInfoText = styled.p`
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.5;
`;

// Sessions list
export const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

export const NoSessionsMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: ${theme.borderRadius.md};
  color: #475569;
`;

export const NoSessionsText = styled.p`
  margin: 0 0 12px 0;
  font-size: 16px;
`;

export const MigrationNotice = styled.p`
  font-size: 14px !important;
  color: #6b7280 !important;
  font-style: italic;
`;

// Session item
export const SessionItem = styled.div<{ $isCurrent?: boolean }>`
  border: 1px solid #e5e7eb;
  border-radius: ${theme.borderRadius.md};
  padding: 16px;
  transition: all ${theme.transitions.normal};
  background-color: ${({ $isCurrent }) => $isCurrent ? '#eff6ff' : 'white'};
  border-color: ${({ $isCurrent }) => $isCurrent ? '#3b82f6' : '#e5e7eb'};

  &:hover {
    border-color: #d1d5db;
    box-shadow: ${theme.shadows.light};
  }
`;

export const SessionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

export const SessionIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
`;

export const SessionDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SessionName = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CurrentBadge = styled.span`
  background-color: #3b82f6;
  color: white;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const SessionMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 14px;
  color: #6b7280;
`;

export const SessionStatus = styled.span`
  font-weight: 500;
`;

export const SessionLoginMethod = styled.span`
  text-transform: capitalize;
`;

export const SessionActions = styled.div`
  flex-shrink: 0;
`;

export const RevokeButton = styled.button<{ $disabled?: boolean }>`
  background-color: ${({ $disabled }) => $disabled ? '#9ca3af' : theme.colors.error};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: background-color ${theme.transitions.normal};

  &:hover:not(:disabled) {
    background-color: #b91c1c;
  }
`;

export const SessionFooter = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
`;

export const SessionInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SessionInfoLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const SessionInfoValue = styled.span`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
`;

// Bulk actions
export const BulkActions = styled.div`
  text-align: center;
  padding: 20px;
  background-color: #f8fafc;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 20px;
`;

export const RevokeAllButton = styled.button<{ $disabled?: boolean }>`
  background-color: ${({ $disabled }) => $disabled ? '#9ca3af' : theme.colors.error};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: ${theme.borderRadius.md};
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: background-color ${theme.transitions.normal};
  margin-bottom: 8px;

  &:hover:not(:disabled) {
    background-color: #b91c1c;
  }
`;

export const BulkActionsNote = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
`;

// Footer
export const SessionManagementFooter = styled.div`
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

export const SecurityNote = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

// Responsive design
export const ResponsiveContainer = styled.div`
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 10px;
    padding: 16px;
  }
`;

export const ResponsiveHeader = styled.div`
  @media (max-width: ${theme.breakpoints.mobile}) {
    h2 {
      font-size: 20px;
    }
  }
`;

export const ResponsiveSessionHeader = styled.div`
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 8px;
  }
`;

export const ResponsiveSessionActions = styled.div`
  @media (max-width: ${theme.breakpoints.mobile}) {
    align-self: flex-end;
  }
`;

export const ResponsiveSessionFooter = styled.div`
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const ResponsiveSessionMeta = styled.div`
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 4px;
  }
`;

// Dark mode support
export const DarkModeContainer = styled.div`
  @media (prefers-color-scheme: dark) {
    background: #1f2937;
    color: #f9fafb;
  }
`;

export const DarkModeHeader = styled.div`
  @media (prefers-color-scheme: dark) {
    border-bottom-color: #374151;
    
    h2 {
      color: #f9fafb;
    }
  }
`;

export const DarkModeSessionItem = styled.div`
  @media (prefers-color-scheme: dark) {
    border-color: #374151;
    background-color: #111827;
    
    &:hover {
      border-color: #4b5563;
    }
    
    &.current-session {
      border-color: #3b82f6;
      background-color: #1e3a8a;
    }
  }
`;

export const DarkModeSessionName = styled.div`
  @media (prefers-color-scheme: dark) {
    color: #f9fafb;
  }
`;

export const DarkModeSessionInfo = styled.div`
  @media (prefers-color-scheme: dark) {
    background-color: #111827;
    border-color: #374151;
    
    p {
      color: #d1d5db;
    }
  }
`;

export const DarkModeSessionFooter = styled.div`
  @media (prefers-color-scheme: dark) {
    border-top-color: #374151;
  }
`;

export const DarkModeSessionInfoValue = styled.span`
  @media (prefers-color-scheme: dark) {
    color: #e5e7eb;
  }
`;

export const DarkModeBulkActions = styled.div`
  @media (prefers-color-scheme: dark) {
    background-color: #111827;
  }
`;

export const DarkModeFooter = styled.div`
  @media (prefers-color-scheme: dark) {
    border-top-color: #374151;
  }
`;
